import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Send, Loader2, TicketIcon, Building2, User } from 'lucide-react';

export default function NewTicket() {
  const navigate = useNavigate();
  const { API } = useAuth();
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [categories, setCategories] = useState({});
  const [customers, setCustomers] = useState([]);
  const [circuits, setCircuits] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [form, setForm] = useState({
    customer_id: '',
    circuit_id: '',
    category: '',
    sub_category: '',
    subject: '',
    description: '',
    priority: 'medium',
    assigned_to: ''
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [categoriesRes, customersRes, staffRes] = await Promise.all([
        fetch(`${API}/tickets/categories`),
        fetch(`${API}/customers`, { headers }),
        fetch(`${API}/tickets/staff-list`, { headers })
      ]);

      const categoriesData = await categoriesRes.json();
      const customersData = await customersRes.json();
      const staffData = await staffRes.json();

      setCategories(categoriesData.categories || {});
      setCustomers(customersData || []);
      setStaffList(staffData.staff || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setDataLoading(false);
    }
  };

  const fetchCircuits = async (customerId) => {
    if (!customerId) {
      setCircuits([]);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/circuits?customer_id=${customerId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setCircuits(data || []);
    } catch (error) {
      console.error('Failed to fetch circuits');
    }
  };

  const handleCustomerChange = (customerId) => {
    setForm({ ...form, customer_id: customerId, circuit_id: '' });
    fetchCircuits(customerId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.customer_id || !form.category || !form.sub_category || !form.subject || !form.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/tickets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });

      const data = await res.json();

      if (res.ok) {
        toast.success(`Ticket ${data.ticket.ticket_number} created successfully!`);
        navigate(`/tickets/${data.ticket.ticket_id}`);
      } else {
        toast.error(data.detail || 'Failed to create ticket');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedCategory = categories[form.category];

  if (dataLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/tickets')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Tickets
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <TicketIcon className="h-5 w-5 text-slate-600" />
            </div>
            <div>
              <CardTitle>Create New Ticket</CardTitle>
              <CardDescription>Create a support ticket on behalf of a customer</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Customer & Circuit Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" /> Customer *
                </Label>
                <Select value={form.customer_id} onValueChange={handleCustomerChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(customer => (
                      <SelectItem key={customer.customer_id} value={customer.customer_id}>
                        {customer.company_name} ({customer.customer_code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Circuit (Optional)</Label>
                <Select 
                  value={form.circuit_id} 
                  onValueChange={(v) => setForm({ ...form, circuit_id: v })}
                  disabled={!form.customer_id}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={form.customer_id ? "Select circuit" : "Select customer first"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific circuit</SelectItem>
                    {circuits.map(circuit => (
                      <SelectItem key={circuit.circuit_id} value={circuit.circuit_id}>
                        {circuit.ant_circuit_id || circuit.circuit_code || circuit.circuit_id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Category & Sub-category */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select 
                  value={form.category} 
                  onValueChange={(v) => setForm({ ...form, category: v, sub_category: '' })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(categories).map(([key, cat]) => (
                      <SelectItem key={key} value={key}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Sub-category *</Label>
                <Select 
                  value={form.sub_category} 
                  onValueChange={(v) => setForm({ ...form, sub_category: v })}
                  disabled={!form.category}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={form.category ? "Select sub-category" : "Select category first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedCategory?.sub_categories?.map(sub => (
                      <SelectItem key={sub.value} value={sub.value}>{sub.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Priority & Assignment */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority *</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <User className="h-4 w-4" /> Assign To (Optional)
                </Label>
                <Select 
                  value={form.assigned_to} 
                  onValueChange={(v) => setForm({ ...form, assigned_to: v === 'unassigned' ? '' : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Leave unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Leave Unassigned</SelectItem>
                    {staffList.map(staff => (
                      <SelectItem key={staff.email} value={staff.email}>
                        {staff.full_name} ({staff.roles?.join(', ')})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label>Subject *</Label>
              <Input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Brief description of the issue"
                maxLength={200}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Provide detailed information about the issue..."
                rows={5}
              />
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => navigate('/tickets')}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={loading}>
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating...</>
                ) : (
                  <><Send className="mr-2 h-4 w-4" /> Create Ticket</>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
